import { useTranslation } from "react-i18next";
import { InvoiceDto } from "@/types/api/api-types";
import { createColumnHelper } from "@tanstack/react-table";
import { ImportIcon } from "lucide-react";
import { InvoicePaymentStatusBadge } from "@/components/shared/badge/invoicePaymentStatus.badge";
import { Dispatch, SetStateAction } from "react";
import { formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import Pdf from "@/assets/icons/pdf-2.svg?react";
import { Table } from "lucide-react";
import { FileCog } from "lucide-react";
import { FileUp } from "lucide-react";

const columnHelper = createColumnHelper<InvoiceDto>();

export const useDeletedInvoiceTableColumns = (
  displayDocument: (invoiceKey: string) => Promise<void>,
  setInvoiceDeleteModal: Dispatch<SetStateAction<string | null>>,
) => {
  const { t } = useTranslation("boInvoices");
  return [
    columnHelper.display({
      id: "client",
      header: t("deletedTable.columns.client"),
      cell: (info) => (
        <div className="max-w-[300px] truncate">
          {info.row.original.billingEntity.company.businessName}
        </div>
      ),
    }),
    columnHelper.display({
      id: "invoice",
      header: t("deletedTable.columns.invoice"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          {info.row.original.type === "AUTO" ? (
            <FileCog width={20} height={20} />
          ) : (
            <FileUp width={20} height={20} />
          )}
          <span className="max-w-[400px] truncate text-sm font-medium text-gray-900">
            {t("deletedTable.invoiceName", {
              invoiceId: info.row.original.invoiceId,
              companyName: info.row.original.billingEntity.companyName,
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "amount",
      header: t("deletedTable.columns.amount"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          <span className="text-sm font-medium text-gray-900">
            {(info.row.original.amountToBill / 100).toLocaleString("fr-FR", {
              style: "currency",
              currency: "EUR",
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "deletedAt",
      header: t("deletedTable.columns.deletedAt"),
      cell: (info) =>
        formatDate(info.row.original.deletedAt, "dd MMM yyyy à HH:mm", {
          locale: fr,
        }),
    }),
    columnHelper.display({
      id: "updatedBy",
      header: t("deletedTable.columns.updatedBy"),
      cell: (info) => {
        if (!info.row.original.updatedBy) return "";
        return `${info.row.original.updatedBy?.firstName.slice(0, 1).toUpperCase()}${info.row.original.updatedBy?.lastName.slice(0, 1).toUpperCase()}`;
      },
    }),
    columnHelper.display({
      id: "status",
      header: t("deletedTable.columns.status"),
      meta: `justify-center`,
      cell: (info) => <InvoicePaymentStatusBadge status={info.row.original.paymentStatus} />,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="grid w-[170px] grid-cols-5 items-center justify-center gap-3">
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() => displayDocument(info.row.original.invoiceKey)}
          >
            <Table width={20} height={20} color="#06F" />
          </button>
          {info.row.original.invoicePDFKey ? (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => displayDocument(info.row.original.invoicePDFKey!)}
            >
              <Pdf width={20} height={20} color="#06F" />
            </button>
          ) : (
            <div />
          )}
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() => setInvoiceDeleteModal(info.row.original.id)}
          >
            <ImportIcon width={20} height={20} className="text-blue-700" />
          </button>
        </div>
      ),
    }),
  ];
};
